import clsx from "clsx";
import ja from "date-fns/locale/ja";
import React, { ReactNode, SyntheticEvent, useEffect, useState } from "react";
import DatePicker, {
  CalendarContainer,
  ReactDatePickerCustomHeaderProps,
  registerLocale,
} from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import useOnClickOutside from "use-onclickoutside";
import * as Popper from "@popperjs/core";

import { Button } from "@/components/atoms/buttons/Button";
import { isEmptyValue, zeroPadding } from "@/utils/common";
import { format, formatForCompareDate } from "@/utils/date";

import IconCalendar from "/public/icon/calendar.svg";

interface Props {
  id?: string;
  onChange: (date: Date | null, event?: SyntheticEvent<any, Event>) => void;
  onFocus?: (event?: SyntheticEvent<any, Event>) => void;
  onBlur?: (event?: SyntheticEvent<any, Event>) => void;
  handleClear?: (event?: SyntheticEvent<any, Event>) => void;
  isShowClearBtn?: boolean;
  selected?: Date | null;
  placeholder?: string;
  hasError?: boolean;
  selectsStart?: boolean;
  selectsEnd?: boolean;
  showTimeSelect?: boolean;
  startDate?: Date | null;
  endDate?: Date | null;
  minDate?: Date | null;
  maxDate?: Date | null;
  formatType?: string;
  className?: string;
  disabled?: boolean;
  popperPlacement?: Popper.Placement;
  hideCalendarIcon?: boolean;
  showMonthYearPicker?: boolean;
}

const DefaultDateFormat = "yyyy/MM/dd";

export default function InputDate({
  id,
  onChange,
  onFocus,
  onBlur,
  handleClear,
  selected,
  placeholder = "入力なし",
  isShowClearBtn = true,
  hasError,
  selectsStart,
  selectsEnd,
  showTimeSelect,
  startDate,
  endDate,
  minDate,
  maxDate,
  formatType,
  className,
  disabled,
  popperPlacement,
  hideCalendarIcon,
  showMonthYearPicker,
}: Props) {
  maxDate = maxDate ?? new Date("9999/12/31"); //入力文字数制御のため、意図的に設定しない限りmaxは9999年末日となる
  const [isOpen, setIsOpen] = useState(false);
  const border = hasError ? "border-accent" : "border-[#A1A6AC]";
  registerLocale("ja", ja);
  const FormatDate = formatType || DefaultDateFormat;

  const submitHandle = () => {
    setIsOpen(false);
  };

  const changeHandle = (selectedDate: Date | null) => {
    onChange(selectedDate ? trimDate(selectedDate) : selectedDate);
    setIsOpen(false);
  };

  const MyContainer: ({
    children,
  }: {
    children: ReactNode;
    className: string;
  }) => JSX.Element = ({ children, className }) => {
    return (
      <div className="p-4 bg-white rounded-lg border-0 react-datepicker-shadow">
        <CalendarContainer className={className}>
          <div className="relative flex">{children}</div>
          <div className="text-right">
            {isShowClearBtn && (
              <div className="inline-block mt-4">
                <button
                  type="button"
                  onClick={(event) =>
                    handleClear ? handleClear(event) : onChange(null, event)
                  }
                  className="py-2 px-4 text-sm font-bold text-theme bg-theme-light/0 hover:bg-theme-thin focus:bg-theme-light rounded"
                >
                  クリア
                </button>
              </div>
            )}
            <div className="inline-block mt-2 ml-4">
              <Button size="sm" onClick={submitHandle} label="決定" />
            </div>
          </div>
        </CalendarContainer>
      </div>
    );
  };

  /**
   * maxDate,minDateを超える場合は丸める
   * @param selectedDate
   */
  const trimDate = (selectedDate: Date) => {
    // if (maxDate && maxDate < selectedDate) {
    //   return maxDate;
    // }
    // if (minDate && minDate > selectedDate) {
    //   return minDate;
    // }
    return selectedDate;
  };

  /**
   * selectedの値をdateTextValueに入れられる形にフォーマット
   */
  const formatSelectedToText = (s: Date | null | undefined) => {
    if (s) {
      return format(trimDate(s), FormatDate);
    }
    return undefined;
  };
  const [dateTextValue, setDateTextValue] = useState<string | undefined>(
    formatSelectedToText(selected),
  );
  const [isTyping, setIsTyping] = useState<boolean>(false);
  useEffect(() => {
    if (!isTyping) {
      setDateTextValue(formatSelectedToText(selected));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);
  useEffect(() => {
    setIsTyping(false);
  }, [isOpen]);

  /**
   * 入力された文字列をカレンダー入力の形にフォーマットする
   * @param dateText
   * @returns
   */
  const valueTextFormat = (dateText: string): string => {
    // 一文字も入力してない場合、nullを返す
    if (dateText?.length === 0) {
      changeHandle(null);
      return "";
    }
    // 先に"-"と"."を”/"に変換
    let changeText = dateText.split("-").join("/");
    changeText = changeText.split(".").join("/");

    // 数字のみの場合、桁数によって年のみ返すか、月、日を抽出してフォーマットする("20230101"をコピペした場合等)
    if (!changeText.includes("/") && changeText.length > 4) {
      // 「月」までフォーマットし直す
      changeText = `${changeText.substring(0, 4)}/${changeText.substring(4)}`;
      // 「日」までフォーマットし直す
      if (changeText.length > 7) {
        changeText = `${changeText.substring(0, 7)}/${changeText.substring(7)}`;
      }
    }

    // "/"区切りで年、月、日を抽出
    const [sy, sm, sd]: string[] = changeText.split("/");

    // 数字のみ入力の場合で月、日にフォーマットする桁数に達していない場合、そのまま返す
    if (sm === undefined && sd === undefined) {
      return sy;
    }

    // 「年」をフォーマット
    // 年数が4桁を超えるときと空の時は、今年の年数を入れる
    let y = sy;
    const thisYear = format(new Date(), "yyyy");
    // 年数が4桁を超えるときと空の時は、今年の年数を入れる
    if (y.length > 4 || y.length === 0) {
      if (y.length > 4 && Number(y) < 10000) {
        // 先頭の文字がゼロの時は末尾4文字
        y = y.slice(-4);
      } else {
        y = thisYear;
      }
    }

    // 「月」まで入力されている場合 ----------------
    // 「月」をフォーマット
    const m = sd === undefined ? sm.substring(0, 2) : sm.slice(-2);
    if (sd === undefined) {
      const trimedData = format(
        trimDate(
          formatForCompareDate({ y: y, m: m, d: undefined, round: "max" }),
        ),
        FormatDate,
      );
      const [ty, tm] = trimedData.split("/");
      const zy =
        Number(y) === 0
          ? zeroPadding("0", y.length)
          : zeroPadding(ty, y.length);
      if (sm.length === 0) {
        return `${zy}/`;
      }
      // 1桁の場合、入力中のためフォーマットせず返す
      if (sm.length === 1) {
        return `${zy}/${m}`;
      }
      if (sm.length < 3) {
        // 2桁の場合、「月」までフォーマットして返す
        const zm = zeroPadding(tm, m.length);
        return `${zy}/${zm}`;
      }
      // 3桁以上の場合、「日」に繰り越す
    }

    // 「日」まで入力されている場合 ----------------
    // 「日」をフォーマット
    const d =
      sd === undefined && sm.length > 2 ? sm.substring(2) : sd.slice(-2);

    const trimedData = format(
      trimDate(formatForCompareDate({ y: y, m: m, d: d, round: "max" })),
      FormatDate,
    );
    const [ty, tm, td] = trimedData.split("/");
    const zy = zeroPadding(ty, y.length);
    const zm = zeroPadding(tm, m.length);
    const zd = zeroPadding(td, d.length);

    // 日付が空の場合、入力中のため年数と月のみ返す
    if (!d || d.length === 0) {
      return `${zy}/${zm}/`;
    }
    // 日付が1桁の場合、入力中のためフォーマットせず返す
    if (d.length < 2) {
      return `${zy}/${zm}/${d}`;
    }
    // 「日」までフォーマットして返す
    return `${zy}/${zm}/${zd}`;
  };

  const datePickerRef = React.useRef(null);
  useOnClickOutside(datePickerRef, () => {
    if (datePickerRef.current) {
      setIsOpen(false);
      if (selected) {
        setDateTextValue(format(selected, FormatDate));
      } else {
        setDateTextValue(undefined);
      }
      setIsTyping(false);
    }
  });

  const renderDatePickerHeader = (params: ReactDatePickerCustomHeaderProps) => {
    return (
      <div>
        <button
          type="button"
          aria-label="Previous Month"
          className={`react-datepicker__navigation react-datepicker__navigation--previous`}
          disabled={params.prevMonthButtonDisabled}
          onClick={() => params.decreaseMonth()}
        >
          <span
            className={
              "react-datepicker__navigation-icon react-datepicker__navigation-icon--previous"
            }
          >
            {"<"}
          </span>
        </button>
        <span className="react-datepicker__current-month">
          {params.monthDate.toLocaleString("ja", {
            month: "long",
            year: "numeric",
          })}
        </span>
        <button
          type="button"
          aria-label="Next Month"
          className={
            "static react-datepicker__navigation react-datepicker__navigation--next"
          }
          disabled={params.nextMonthButtonDisabled}
          onClick={() => params.increaseMonth()}
        >
          <span
            className={
              "react-datepicker__navigation-icon react-datepicker__navigation-icon--next"
            }
          >
            {">"}
          </span>
        </button>
      </div>
    );
  };

  const renderMonthPickerHeader = (
    params: ReactDatePickerCustomHeaderProps,
  ) => {
    return (
      <div>
        <button
          type="button"
          aria-label="Previous Month"
          className={`react-datepicker__navigation react-datepicker__navigation--previous`}
          disabled={params.prevYearButtonDisabled}
          onClick={() => params.decreaseYear()}
        >
          <span
            className={
              "react-datepicker__navigation-icon react-datepicker__navigation-icon--previous"
            }
          >
            {"<"}
          </span>
        </button>
        <span className="react-datepicker__current-month">
          {params.monthDate.getFullYear()}
        </span>
        <button
          type="button"
          aria-label="Next Month"
          className={
            "static react-datepicker__navigation react-datepicker__navigation--next"
          }
          disabled={params.nextYearButtonDisabled}
          onClick={() => params.increaseYear()}
        >
          <span
            className={
              "react-datepicker__navigation-icon react-datepicker__navigation-icon--next"
            }
          >
            {">"}
          </span>
        </button>
      </div>
    );
  };

  return (
    <div className="relative" ref={datePickerRef}>
      <DatePicker
        showMonthYearPicker={showMonthYearPicker}
        id={id}
        open={isOpen}
        onBlur={(e) => {
          setIsTyping(false);
          onBlur && onBlur(e);
        }}
        onFocus={(e) => {
          onFocus && onFocus(e);
          setIsOpen(true);
          setIsTyping(true);
        }}
        onClickOutside={() => {
          setIsOpen(false);
          if (selected) {
            setDateTextValue(format(selected, FormatDate));
          } else {
            setDateTextValue(undefined);
          }
          setIsTyping(false);
        }}
        onChange={changeHandle}
        dateFormat={FormatDate}
        value={dateTextValue}
        selected={selected}
        locale="ja"
        placeholderText={placeholder}
        popperPlacement={popperPlacement}
        disabled={disabled}
        className={clsx(border, "w-full h-11", className)}
        calendarContainer={MyContainer}
        shouldCloseOnSelect={false}
        selectsStart={selectsStart}
        selectsEnd={selectsEnd}
        startDate={startDate}
        showTimeSelect={showTimeSelect}
        endDate={endDate}
        minDate={minDate}
        maxDate={maxDate}
        onKeyDown={(e) => {
          e.preventDefault();
        }}
        onChangeRaw={(e) => {
          let inputVal = e.target.value;
          // 手動入力の時、フォーマットする
          if (inputVal !== undefined) {
            // 半角制御、/、-
            if (inputVal.match(/[^0-9/.-]/) !== null) {
              e.preventDefault();
              return;
            }
            setIsTyping(true);
            inputVal = valueTextFormat(inputVal?.substring(0, 12));
            setDateTextValue(inputVal);
          } else {
            setIsTyping(false);
          }

          // 以下、selectedに反映させる処理
          if (!isEmptyValue(inputVal) && inputVal.match(/^[0-9]+$/)) {
            const [y, m, d] = inputVal.split("/");
            // 日付に変換
            const year = Number(
              !isEmptyValue(y) ? y : format(new Date(), "yyyy"),
            );
            const month = !isEmptyValue(m) ? Number(m) - 1 : 0;
            const day = !isEmptyValue(d) ? Number(d) : 1;
            changeHandle(new Date(year, month, day));
          }
          // YYYY/MM/DDまたはYYYY-MM-DD形式の日付を設定
          if (
            (inputVal?.includes("/") || inputVal?.includes("-")) &&
            !isNaN(new Date(inputVal).getTime())
          ) {
            changeHandle(new Date(inputVal));
          }
        }}
        renderCustomHeader={(params) =>
          showMonthYearPicker
            ? renderMonthPickerHeader(params)
            : renderDatePickerHeader(params)
        }
      ></DatePicker>
      {!hideCalendarIcon && (
        <button
          className={`block absolute top-1/2 right-0 mr-2 w-6 h-6 -translate-y-1/2 ${
            isTyping && !isOpen
              ? "cursor-pointer"
              : "cursor-default pointer-events-none"
          }`}
          onClick={() => {
            if (isTyping && !isOpen) {
              setIsOpen(true);
            }
          }}
        >
          <IconCalendar
            className="w-6 h-6 pointer-events-none"
            fill={disabled ? "#BCBCBC" : "#1A1A1C"}
          />
        </button>
      )}
    </div>
  );
}
